import { z } from 'zod';
import { readPhone, formatPhone } from './phone.js';

export const contactChannelLabels = {
  CALL: 'Ligações',
  WHATSAPP: 'WhatsApp',
  TELEGRAM: 'Telegram',
} as const;
export type ContactChannel = keyof typeof contactChannelLabels;
export const contactNumbersSchema = z
  .array(
    z
      .object({
        label: z.string().trim().min(1, 'Identifique o contato, por exemplo Recepção.').max(60),
        number: z
          .string()
          .trim()
          .max(30)
          .transform((value, ctx) => {
            const phone = readPhone(value);
            if (!phone) {
              ctx.addIssue({
                code: 'custom',
                message: 'Informe um telefone com DDD e código do país.',
              });
              return z.NEVER;
            }
            return phone.e164;
          }),
        channels: z
          .array(z.enum(['CALL', 'WHATSAPP', 'TELEGRAM']))
          .min(1, 'Selecione pelo menos um canal.')
          .max(3)
          .refine((items) => new Set(items).size === items.length, 'Não repita canais.'),
        isPublic: z.boolean(),
      })
      .strict(),
  )
  .max(20, 'Adicione no máximo 20 contatos.')
  .superRefine((items, ctx) => {
    const numbers = new Set<string>();
    items.forEach((item, index) => {
      if (numbers.has(item.number))
        ctx.addIssue({
          code: 'custom',
          path: [index, 'number'],
          message: 'Este número já está cadastrado. Combine os canais no mesmo contato.',
        });
      numbers.add(item.number);
    });
  });
export type ContactNumber = z.infer<typeof contactNumbersSchema>[number];
export function publicContactNumbers(input: unknown): ContactNumber[] {
  return contactNumbersSchema.parse(input).filter((item) => item.isPublic);
}
export function contactNumberUrl(number: string, channel: ContactChannel): string {
  const normalized = readPhone(number)?.e164;
  if (!normalized) return '';
  if (channel === 'CALL') return `tel:${normalized}`;
  if (channel === 'WHATSAPP') return `https://wa.me/${normalized.slice(1)}`;
  return `https://t.me/${normalized}`;
}

/// Compatibilidade com edições antigas do telefone: preserva contatos e outros canais.
export function updateCallContact(
  contacts: ContactNumber[],
  phone: string | null,
): ContactNumber[] {
  const next = contacts
    .map((item) => ({
      ...item,
      channels:
        item.number === phone
          ? [...item.channels]
          : item.channels.filter((channel) => channel !== 'CALL'),
    }))
    .filter((item) => item.channels.length > 0);
  if (!phone) return next;
  const own = next.find((item) => item.number === phone);
  if (own) {
    if (!own.channels.includes('CALL')) own.channels.push('CALL');
  } else next.push({ label: 'Atendimento', number: phone, channels: ['CALL'], isPublic: false });
  return next;
}

export function contactNumbersText(contacts: ContactNumber[]): string[] {
  return contacts.map(
    (contact) =>
      `${contact.label}: ${formatPhone(contact.number) ?? contact.number} · ${contact.channels.map((channel) => contactChannelLabels[channel]).join(' / ')} · ${contact.isPublic ? 'público' : 'privado'}`,
  );
}
